import React, { useState, useEffect, useMemo } from 'react'
import * as Sentry from '@sentry/react'
import { useDepartment } from '../context/DepartmentContext'
import { useToast } from '../context/ToastContext'
import {
  getMorningLogs, getSalesTransactions, getSurplus,
  getFlags, resolveFlag, getAllDailySummaries,
  getProductionLogs, getDistributionLogs, getDamages, getBakerySurplus,
  backgroundSyncForDate, getVorsWithSteak, ensureDailyPricesLoaded, getPapWarmers,
} from '../lib/store'

// Map the steakMode enum to a small inline label for the date header.
// Defaults to 'vors' for legacy/missing data so the badge always shows.
const STEAK_MODE_HEADER_LABEL = {
  vors: '🟢 With Vors',
  no_vors: '🔴 No Vors',
  extra_steak: '🟡 Extra Steak',
}
import { getCategories, getCategoryConfig, UNIT_LABELS } from '../data/categories'
import { BAKERY_CATEGORIES_SEED } from '../data/bakeryData'
import { YAWEHS_CATEGORIES_SEED } from '../data/yawehsData'
import { TUCKSHOP_CATEGORIES_SEED } from '../data/tuckshopData'
import { getProducts, getProductsAsync, getPapConfig } from '../data/products'
import { isMealSale, CROSS_POOL_ALLOC, getDailyPrices, getDynamicCrossPoolAlloc } from '../utils/reconciliationEngine'
import { isVorsSuppressed, vorsDisplayNote } from '../utils/steakModeLogic'
import { computeDiscountShortages } from '../lib/discountShortages'
import { getProductPrice } from '../lib/productPricing'
import {
  calculateProjectedSales, calculateVariance, formatR, formatPct, calcStats,
  buildDailyCategorySummary, getAveragePriceForCategory
} from '../lib/calculations'
import { getSeverityColor, getSeverityBadge } from '../lib/flagging'
import { CheckCircle, AlertTriangle, TrendingUp, TrendingDown, ChevronDown, ChevronRight, Download, FileSpreadsheet } from 'lucide-react'
import DateInput from '../components/ui/DateInput'

function getCategoriesForDept(dept) {
  if (dept === 'yawehs') return YAWEHS_CATEGORIES_SEED
  if (dept === 'bakery') return BAKERY_CATEGORIES_SEED
  if (dept === 'tuckshop') return TUCKSHOP_CATEGORIES_SEED
  return getCategories()
}

// Build a productCode → category name map for bakery/yawehs departments
function buildProductCategoryMap(dept) {
  const products = getProducts(dept)
  const map = {}
  for (const p of products) map[p.code] = p.category
  return map
}

// Default data start date — reconciliation flags are suppressed before this date
const DEFAULT_DATA_START_DATE = '2026-04-01'

export default function DailyOps({ date: dateProp }) {
  const { currentDepartmentId } = useDepartment()
  const { addToast } = useToast()
  const today = new Date().toISOString().split('T')[0]
  const [ownDate, setOwnDate] = useState(today)
  const date = dateProp || ownDate
  const setDate = setOwnDate

  const isBakery = currentDepartmentId === 'bakery' || currentDepartmentId === 'yawehs'

  // Data start date — flags suppressed for dates before this
  const [dataStartDate, setDataStartDate] = useState(DEFAULT_DATA_START_DATE)
  useEffect(() => {
    import('../lib/firestoreRest').then(({ restGet }) => {
      restGet('config/settings').then(cfg => {
        if (cfg?.dataStartDate) setDataStartDate(cfg.dataStartDate)
      }).catch(err => {
        console.warn('[DailyOps] config/settings load failed:', err?.message)
        Sentry.captureException(err, { extra: { fn: 'DailyOps.loadConfig' } })
      })
    }).catch(err => {
      console.warn('[DailyOps] dynamic firestoreRest import failed:', err?.message)
      Sentry.captureException(err, { extra: { fn: 'DailyOps.importFirestoreRest' } })
    })
  }, [])
  const isHistoricalDate = date < dataStartDate
  const flagsSuppressed = isHistoricalDate

  // Live products for meal QPS extraction
  const [liveProducts, setLiveProducts] = useState(() => getProducts(currentDepartmentId))
  useEffect(() => {
    getProductsAsync(currentDepartmentId).then(p => setLiveProducts(p))
  }, [currentDepartmentId])

  // PAP sizing config from Firestore
  const [papConfig, setPapConfig] = useState({ full: 20, half: 12, plate: 10 })
  useEffect(() => {
    getPapConfig().then(c => setPapConfig(c))
  }, [])

  // Sync key to force re-read from localStorage after background sync writes new data
  const [syncKey, setSyncKey] = useState(0)
  const [syncing, setSyncing] = useState(false)

  // Build data synchronously from localStorage (instant render)
  function buildData() {
    const dept = currentDepartmentId
    const categories = getCategoriesForDept(dept)
    const flags = getFlags(dept, { date })

    let morning, surplus, sales, catSummaries

    if (!isBakery) {
      morning = getMorningLogs(dept, date)
      surplus = getSurplus(dept, date)
      sales = getSalesTransactions(dept, date)

      // Cross-pool accumulator: products like FS/HST contribute to multiple pools
      const crossPoolConsumed = {} // { categoryName: { amount, details[] } }

      // Resolve today's piece-cost-driven cross-pool allocation. Falls back
      // to legacy R30/R18 when no morning log has been saved for the day.
      const { steakUP, vorsUP, steakMode } = getDailyPrices(dept, date)
      const dynamicAlloc = getDynamicCrossPoolAlloc(steakUP, vorsUP, steakMode)

      catSummaries = categories.map(cat => {
        const morningEntry = morning.find(m => m.category === cat.name)
        const opening = morningEntry?.value || 0
        const surplusEntry = surplus.find(s => s.category === cat.name)
        const surplusRaw = surplusEntry?.value || 0
        // Convert surplus to rands if entered in units/pieces
        let surplusVal = surplusRaw
        if (cat.surplusUnit === 'units' || cat.openingUnit === 'units') {
          if (cat.type === 'fixed_unit' && cat.unitPrice) {
            surplusVal = surplusRaw * cat.unitPrice
          } else if (cat.type === 'discrete') {
            surplusVal = surplusRaw * getAveragePriceForCategory(cat.name, currentDepartmentId)
          }
        }
        const catSalesFiltered = sales.filter(s => s.category === cat.name)

        let actualSales = 0
        let expectedSales = 0
        let totalPosActual = 0
        let totalPosExpected = 0
        const salesDetail = []
        for (const s of catSalesFiltered) {
          const prod = liveProducts.find(p => p.code?.toLowerCase() === s.productCode?.toLowerCase())
          const qty = Number(s.qtySold) || 0
          const actualRands = Number(s.salesRands) || 0
          const isMeal = prod ? isMealSale(prod.name, prod.code) : false
          let meatConsumed = 0
          let formula = ''

          // Cross-pool allocation: FS/HST contribute to both steak and vors
          // pools. Dynamic alloc (driven by today's piece costs + vors toggle)
          // takes priority over per-product overrides and the legacy hardcode.
          const crossAlloc = dynamicAlloc[prod?.code] || prod?.crossPoolAlloc || CROSS_POOL_ALLOC[prod?.code]
          if (crossAlloc) {
            const alloc = crossAlloc
            const thisCatAlloc = alloc[cat.name] || 0
            meatConsumed = qty * thisCatAlloc
            formula = `${qty} × R${thisCatAlloc} (${cat.name} alloc) = ${formatR(meatConsumed)}`
            // Send allocations to other pools
            for (const [poolName, poolAmount] of Object.entries(alloc)) {
              if (poolName !== cat.name && poolAmount > 0) {
                if (!crossPoolConsumed[poolName]) crossPoolConsumed[poolName] = { amount: 0, details: [] }
                crossPoolConsumed[poolName].amount += qty * poolAmount
                crossPoolConsumed[poolName].details.push({
                  code: s.productCode,
                  name: prod.name,
                  qty,
                  allocPerUnit: poolAmount,
                  total: qty * poolAmount,
                  sourceCategory: cat.name,
                })
              }
            }
          } else if (!prod) {
            meatConsumed = actualRands
            formula = `${formatR(actualRands)} (no product match)`
          } else if (prod.saleType === 'continuous_mirror') {
            meatConsumed = actualRands
            formula = `${formatR(actualRands)} direct`
          } else if (isMeal && prod.saleType === 'continuous_fixed') {
            meatConsumed = qty * (Number(prod.qps) || 0)
            formula = `${qty} × ${prod.qps} QPS = ${formatR(meatConsumed)}`
          } else if (isMeal) {
            // Discrete meal bundle: qps = pieces per serving (not rands).
            // Pool depletion = total pieces × base piece cost for the
            // category. This matches reconcileCategory's discrete branch
            // and keeps consumed in the same units as projected/surplus.
            const basePieceCost = getAveragePriceForCategory(cat.name, dept)
            const pieces = qty * (Number(prod.qps) || 1)
            meatConsumed = pieces * basePieceCost
            formula = `${qty} × ${prod.qps || 1} pieces × ${formatR(basePieceCost)}/piece = ${formatR(meatConsumed)}`
          } else {
            meatConsumed = actualRands
            formula = `${formatR(actualRands)} POS direct`
          }
          // posExpected: only compute for products with a fixed unit price.
          // continuous_mirror products have no fixed price — posExpected = posActual (no variance possible).
          const branchPrice = prod ? getProductPrice(prod, dept) : null
          const posExpected = (!prod || prod.saleType === 'continuous_mirror' || !branchPrice)
            ? actualRands
            : qty * Number(branchPrice)
          actualSales += meatConsumed
          expectedSales += meatConsumed
          totalPosActual += actualRands
          totalPosExpected += posExpected
          salesDetail.push({
            code: s.productCode,
            name: prod?.name || s.productName || s.productCode,
            qty,
            unitPrice: branchPrice ?? prod?.unitPrice,
            qps: prod?.qps,
            meatAmount: meatConsumed,
            expectedAmount: posExpected,
            posAmount: actualRands,
            formula,
          })
        }

        return {
          ...buildDailyCategorySummary(cat.name, opening, surplusVal, actualSales),
          label: cat.label,
          openingUnit: cat.openingUnit,
          morningEntry,
          surplusEntry,
          salesDetail,
          expectedSales,
          totalPosActual,
          totalPosExpected,
          salesVariance: totalPosExpected - totalPosActual,
        }
      })

      // Second pass: apply cross-pool contributions (e.g., FS steak sales → vors pool)
      for (const [poolName, { amount, details }] of Object.entries(crossPoolConsumed)) {
        const idx = catSummaries.findIndex(c => c.category === poolName)
        if (idx >= 0) {
          const c = catSummaries[idx]
          const newConsumed = c.consumed + amount
          const newVariance = c.projectedSales !== null ? c.projectedSales - newConsumed - c.surplusValue : null
          catSummaries[idx] = {
            ...c,
            consumed: newConsumed,
            actualSales: newConsumed,
            variance: newVariance,
            reconStatus: newVariance !== null ? (newVariance > 2 ? 'short' : 'reconciled') : null,
            crossPoolDetails: details,
          }
        }
      }

      // Vors suppression: when the kitchen isn't serving vors today
      // (no_vors or extra_steak), keep the vors row visible (so staff
      // can see it's there) but clear its status flag and add a label
      // explaining the R0 result. The rand math is already correct
      // because dynamicAlloc has vors:0 in non-vors modes.
      if (isVorsSuppressed(steakMode)) {
        const vorsIdx = catSummaries.findIndex(c => c.category === 'vors')
        if (vorsIdx >= 0) {
          catSummaries[vorsIdx] = {
            ...catSummaries[vorsIdx],
            displayNote: vorsDisplayNote(steakMode),
            reconStatus: null,
          }
        }
      }
    } else {
      const pcMap = buildProductCategoryMap(dept)
      const production = getProductionLogs(date, null, dept)
      const distribution = getDistributionLogs(date, null, dept)
      const damages = getDamages(date, dept)
      const bakerySurplus = getBakerySurplus(date, dept)

      const prodByCategory = {}
      for (const p of production) {
        const cat = pcMap[p.productCode] || ''
        prodByCategory[cat] = (prodByCategory[cat] || 0) + (Number(p.qtyProduced) || 0)
      }

      morning = categories
        .map(cat => prodByCategory[cat.name]
          ? { category: cat.name, value: prodByCategory[cat.name], unit: cat.openingUnit }
          : null)
        .filter(Boolean)

      const surplusByCategory = {}
      for (const s of bakerySurplus) {
        const cat = pcMap[s.productCode] || ''
        surplusByCategory[cat] = (surplusByCategory[cat] || 0) + (Number(s.actualSurplus) || 0)
      }

      surplus = categories
        .map(cat => surplusByCategory[cat.name]
          ? { category: cat.name, value: surplusByCategory[cat.name], unit: cat.surplusUnit }
          : null)
        .filter(Boolean)

      const salesByCategory = {}
      for (const d of distribution) {
        const cat = pcMap[d.productCode] || ''
        salesByCategory[cat] = (salesByCategory[cat] || 0) + (Number(d.salesRands) || 0)
      }

      const products = getProducts(dept)
      const productNameMap = {}
      const productPriceMap = {}
      for (const p of products) {
        productNameMap[p.code] = p.name
        const branchPrice = getProductPrice(p, dept)
        productPriceMap[p.code] = branchPrice == null ? 0 : branchPrice
      }
      sales = distribution.map(d => ({
        ...d,
        category: pcMap[d.productCode] || '',
        productName: productNameMap[d.productCode] || d.productCode,
        unitPrice: productPriceMap[d.productCode] || 0,
        salesRands: Number(d.salesRands) || 0,
      }))

      const damagesByCategory = {}
      for (const d of damages) {
        const cat = pcMap[d.productCode] || ''
        damagesByCategory[cat] = (damagesByCategory[cat] || 0) + (Number(d.qty) || 0)
      }

      catSummaries = categories.map(cat => {
        const opening = prodByCategory[cat.name] || 0
        const surplusVal = surplusByCategory[cat.name] || 0
        const damagesVal = damagesByCategory[cat.name] || 0
        const actualSales = salesByCategory[cat.name] || 0

        return {
          ...buildDailyCategorySummary(cat.name, opening, surplusVal, actualSales),
          label: cat.label,
          openingUnit: cat.openingUnit,
          damagesValue: damagesVal,
        }
      })
    }

    const totalSales = isBakery
      ? catSummaries.reduce((sum, c) => sum + (c.actualSales || 0), 0)
      : sales.reduce((sum, s) => sum + (s.salesRands || 0), 0)

    const summaries = getAllDailySummaries(dept)
    const last30 = summaries
      .filter(s => s.date < date)
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 30)
    const avg30 = last30.length > 0
      ? last30.reduce((s, d) => s + (d.totalSales || 0), 0) / last30.length
      : 0

    // PAP breakdown: calculate PAP contribution from all meal sales
    let papFromMeals = 0
    const papMealDetails = []
    if (!isBakery) {
      for (const s of sales) {
        const prod = liveProducts.find(p => p.code?.toLowerCase() === s.productCode?.toLowerCase())
        if (!prod || !isMealSale(prod.name, prod.code)) continue
        const qty = Number(s.qtySold) || 0
        const name = (prod.name || '').toLowerCase()
        let size = 'plate'
        let cost = papConfig.plate
        if (name.includes('full')) { size = 'full'; cost = papConfig.full }
        else if (name.includes('half') || name.includes('hafu')) { size = 'half'; cost = papConfig.half }
        const contribution = qty * cost
        papFromMeals += contribution
        papMealDetails.push({ code: prod.code, name: prod.name, qty, size, cost, contribution })
      }
    }
    // PAP standalone: consumed value from the pap category
    const papStandalone = catSummaries.find(c => c.category === 'pap only')?.consumed || 0
    const papTotal = papFromMeals + papStandalone

    return { morning, surplus, sales, flags, catSummaries, totalSales, avg30, papFromMeals, papStandalone, papTotal, papMealDetails }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = useMemo(() => buildData(), [currentDepartmentId, date, liveProducts, syncKey, papConfig])

  // Pre-load steak/vors unit prices into the localStorage cache so
  // getDailyPrices returns the staff-entered values on first render
  // (instead of falling back to the legacy R30/R18). Runs in parallel
  // with backgroundSyncForDate — both populate the same cache keys, so
  // whichever finishes first wins. This is the cross-device safety net:
  // a phone that has never run saveMorningLog locally for this date
  // would otherwise show stale prices until backgroundSync finished.
  useEffect(() => {
    let cancelled = false
    ensureDailyPricesLoaded(currentDepartmentId, date).then(() => {
      if (!cancelled) setSyncKey(k => k + 1)
    }).catch(err => {
      console.warn('[DAILYOPS] ensureDailyPricesLoaded error:', err?.message)
    })
    return () => { cancelled = true }
  }, [currentDepartmentId, date])

  // Background sync: after initial render, always fetch from Firestore
  useEffect(() => {
    let cancelled = false
    setSyncing(true)
    backgroundSyncForDate(currentDepartmentId, date).then(changed => {
      if (!cancelled) {
        setSyncing(false)
        if (changed) setSyncKey(k => k + 1) // trigger re-read from localStorage
      }
    }).catch(err => {
      console.error('[DAILYOPS] sync error:', err)
      if (!cancelled) setSyncing(false)
    })
    return () => { cancelled = true }
  }, [currentDepartmentId, date])

  const handleResolve = (flagId) => {
    resolveFlag(flagId)
    addToast('Flag resolved', 'success')
  }

  const [expandedCat, setExpandedCat] = useState(null)

  // jsPDF only supports basic Latin in default Helvetica. Replace Unicode chars with ASCII.
  const pdfSafe = (str) => String(str || '')
    .replace(/\u2014/g, '-')   // em dash → hyphen
    .replace(/\u2013/g, '-')   // en dash → hyphen
    .replace(/\u2212/g, '-')   // minus sign → hyphen
    .replace(/\u2192/g, '->')  // right arrow → ->
    .replace(/\u2019/g, "'")   // right quote → apostrophe
    .replace(/\u2018/g, "'")   // left quote → apostrophe
    .replace(/\u201C/g, '"')   // left double quote
    .replace(/\u201D/g, '"')   // right double quote
    .replace(/[^\x00-\x7F]/g, '') // strip any remaining non-ASCII

  // Format for PDF: always returns ASCII-safe string
  const pdfR = (value) => pdfSafe(formatR(value))

  const downloadBreakdownPdf = async (c) => {
    try {
      const { default: jsPDF } = await import('jspdf')
      const { default: autoTable } = await import('jspdf-autotable')
      const doc = new jsPDF()
      const pw = doc.internal.pageSize.width
      let y = 20

      // Header
      doc.setFontSize(16)
      doc.setFont('helvetica', 'bold')
      doc.text('DESRES KITCHEN AND CONFECTIONERY', pw / 2, y, { align: 'center' })
      y += 8
      doc.setFontSize(11)
      doc.setFont('helvetica', 'normal')
      doc.text(pdfSafe(`Category Reconciliation Breakdown - ${c.label}`), pw / 2, y, { align: 'center' })
      y += 6
      doc.setFontSize(9)
      doc.text(`Date: ${date} | Department: ${currentDepartmentId}`, pw / 2, y, { align: 'center' })
      y += 12

      // 1. Short equation
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.text('Reconciliation', 14, y)
      y += 7
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      doc.text(`Projected (${pdfR(c.projectedSales)}) - Consumed (${pdfR(c.consumed)}) - Surplus (${pdfR(c.surplusValue)}) = ${pdfR(c.variance)} ${c.variance > 2 ? 'SHORT' : 'RECONCILED'}`, 14, y)
      y += 10

      // 2. Morning Log detail
      doc.setFont('helvetica', 'bold')
      doc.text('Morning Log', 14, y)
      y += 7
      doc.setFont('helvetica', 'normal')
      doc.text(`Opening: ${c.openingValue} ${c.openingUnit || ''} -> Projected: ${pdfR(c.projectedSales)}`, 14, y)
      y += 10

      // 3. Sales breakdown
      doc.setFont('helvetica', 'bold')
      doc.text(`Sales Breakdown (${(c.salesDetail || []).length} transactions)`, 14, y)
      y += 3
      if (c.salesDetail && c.salesDetail.length > 0) {
        autoTable(doc, {
          startY: y,
          head: [['Code', 'Product', 'Qty', 'Expected', 'Actual (POS)', 'Diff', 'Formula']],
          body: c.salesDetail.map(d => {
            // Actual (POS) = sale.salesRands (what the customer paid).
            // Not the pool-alloc meatAmount used in reconciliation math.
            const actualPos = d.posAmount ?? 0
            const saleDiff = (d.expectedAmount || 0) - actualPos
            return [
              pdfSafe(d.code), pdfSafe(d.name), d.qty,
              pdfR(d.expectedAmount || 0),
              pdfR(actualPos),
              saleDiff !== 0 ? `${saleDiff > 0 ? '-' : '+'}${pdfR(Math.abs(saleDiff))}` : '-',
              pdfSafe(d.formula)
            ]
          }),
          styles: { fontSize: 8 },
          headStyles: { fillColor: [60, 60, 60] },
          margin: { left: 14, right: 14 },
        })
        y = doc.lastAutoTable.finalY + 5
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(9)
        doc.text(`Total Expected: ${pdfR(c.totalPosExpected || 0)}  |  Total Actual (POS): ${pdfR(c.totalPosActual || 0)}  |  Sales Variance: ${pdfR(c.salesVariance || 0)}`, 14, y)
        y += 10
      } else {
        y += 5
        doc.setFont('helvetica', 'normal')
        doc.text('No sales transactions for this category.', 14, y)
        y += 10
      }

      // 4. Surplus detail
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(10)
      doc.text('Surplus', 14, y)
      y += 7
      doc.setFont('helvetica', 'normal')
      doc.text(c.surplusValue > 0 ? `Surplus: ${pdfR(c.surplusValue)}` : 'No surplus recorded.', 14, y)
      y += 10

      // 5. Net summary
      doc.setFont('helvetica', 'bold')
      doc.text('Net Summary', 14, y)
      y += 7
      doc.setFont('helvetica', 'normal')
      const statusText = c.variance > 2 ? 'This amount is unaccounted for.' : 'Category is reconciled.'
      doc.text(`Projected ${pdfR(c.projectedSales)} - Consumed ${pdfR(c.consumed)} - Surplus ${pdfR(c.surplusValue)} = ${pdfR(c.variance)}. ${statusText}`, 14, y)
      if ((c.salesVariance || 0) > 0) {
        y += 6
        doc.text(`Sales variance: ${pdfR(c.salesVariance)} under-collected vs expected prices.`, 14, y)
      }
      y += 15

      // Footer
      doc.setFontSize(8)
      doc.setTextColor(150)
      doc.text(`Generated by Desres OS - ${new Date().toLocaleString()}`, pw / 2, doc.internal.pageSize.height - 10, { align: 'center' })

      doc.save(`reconciliation-${c.category}-${date}.pdf`)
    } catch (err) {
      console.error('PDF generation failed:', err)
      addToast('PDF export failed', 'error')
    }
  }

  const downloadShortfallReportPdf = async (allCategories, shortCats, discountCats, allSales, productList) => {
    // If called with old signature (single array), derive short/discount from it
    if (!shortCats) {
      shortCats = allCategories.filter(c => c.reconStatus === 'short')
      discountCats = allCategories.filter(c => (c.salesVariance || 0) < -2)
    }
    const totalShortfall = shortCats.reduce((s, c) => s + (c.variance || 0), 0)

    // Discount shortages: use the canonical helper so the per-product
    // breakdown and the summary total are derived from the same source.
    // The legacy approach computed totalDiscountLoss from category-level
    // salesVariance and per-row loss > R2, which mismatched and could
    // print a non-zero total against an empty body.
    const discountResult = computeDiscountShortages(allSales || [], productList || [], currentDepartmentId)
    const totalDiscountLoss = discountResult.totalDiscount
    const discountItems = discountResult.items
    const freeItems = discountResult.freeItems
    const totalFreeLoss = discountResult.totalFreeLoss

    const totalUnaccounted = totalShortfall + totalDiscountLoss + totalFreeLoss
    const hasAnyLoss = shortCats.length > 0 || discountItems.length > 0 || freeItems.length > 0

    try {
      const { default: jsPDF } = await import('jspdf')
      const { default: autoTable } = await import('jspdf-autotable')
      const doc = new jsPDF()
      const pw = doc.internal.pageSize.width
      const ph = doc.internal.pageSize.height
      let y = 20

      // Header
      doc.setFontSize(16)
      doc.setFont('helvetica', 'bold')
      doc.text('DESRES KITCHEN AND CONFECTIONERY', pw / 2, y, { align: 'center' })
      y += 8
      doc.setFontSize(12)
      doc.text('Daily Shortfall Report', pw / 2, y, { align: 'center' })
      y += 6
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.text(`Date: ${date} | Department: ${currentDepartmentId}`, pw / 2, y, { align: 'center' })
      y += 10

      if (!hasAnyLoss) {
        doc.setFontSize(11)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(0, 150, 0)
        doc.text('All categories reconciled. No shortfalls or discount losses recorded today.', pw / 2, y, { align: 'center' })
        doc.setTextColor(0)
      } else {
        // ── SECTION 1: STOCK SHORTFALLS ──
        if (shortCats.length > 0) {
          doc.setFont('helvetica', 'bold')
          doc.setFontSize(11)
          doc.setTextColor(180, 50, 40)
          doc.text('STOCK SHORTFALLS - Missing Stock', 14, y)
          doc.setTextColor(0)
          y += 3
          autoTable(doc, {
            startY: y,
            head: [['Category', 'Projected (R)', 'Surplus (R)', 'Consumed (R)', 'Shortfall (R)']],
            body: [
              ...shortCats.map(c => [
                pdfSafe(c.label), pdfR(c.projectedSales), pdfR(c.surplusValue),
                pdfR(c.consumed), pdfR(c.variance)
              ]),
              [{ content: 'TOTAL STOCK SHORTFALL', styles: { fontStyle: 'bold' } }, '', '', '',
               { content: pdfR(totalShortfall), styles: { fontStyle: 'bold' } }]
            ],
            styles: { fontSize: 9 },
            headStyles: { fillColor: [180, 50, 40] },
            margin: { left: 14, right: 14 },
          })
          y = doc.lastAutoTable.finalY + 10
        }

        // ── SECTION 2: DISCOUNT LOSSES ──
        // Per-product breakdown computed by computeDiscountShortages.
        // The body and the summary total now derive from the same source,
        // so a non-zero total can never appear against an empty body.
        if (discountItems.length > 0) {
          if (y > ph - 80) { doc.addPage(); y = 20 }
          doc.setFont('helvetica', 'bold')
          doc.setFontSize(11)
          doc.setTextColor(200, 150, 0)
          doc.text('DISCOUNT LOSSES - Sold Below Price', 14, y)
          doc.setTextColor(0)
          y += 3

          autoTable(doc, {
            startY: y,
            head: [['Code', 'Product', 'Qty', 'Expected (R)', 'Actual (R)', 'Lost (R)']],
            body: [
              ...discountItems.map(item => [
                pdfSafe(item.productCode),
                pdfSafe(item.productName),
                item.qty,
                pdfR(item.expectedPrice),
                pdfR(item.actualPrice),
                pdfR(item.totalDiscount),
              ]),
              [{ content: 'TOTAL DISCOUNT LOSS', styles: { fontStyle: 'bold' } }, '', '', '', '',
               { content: pdfR(totalDiscountLoss), styles: { fontStyle: 'bold' } }]
            ],
            styles: { fontSize: 8 },
            headStyles: { fillColor: [200, 150, 0] },
            margin: { left: 14, right: 14 },
          })
          y = doc.lastAutoTable.finalY + 10
        }

        // ── SECTION 2b: FREE ITEMS (given away) ──
        if (freeItems.length > 0) {
          if (y > ph - 80) { doc.addPage(); y = 20 }
          doc.setFont('helvetica', 'bold')
          doc.setFontSize(11)
          doc.setTextColor(200, 100, 0)
          doc.text('ITEMS GIVEN AWAY FREE', 14, y)
          doc.setTextColor(0)
          y += 3
          autoTable(doc, {
            startY: y,
            head: [['Code', 'Product', 'Qty', 'Expected (R)', 'Loss (R)']],
            body: [
              ...freeItems.map(item => [
                pdfSafe(item.productCode),
                pdfSafe(item.productName),
                item.qty,
                pdfR(item.expectedPrice),
                pdfR(item.totalLoss),
              ]),
              [{ content: 'TOTAL FREE-ITEM LOSS', styles: { fontStyle: 'bold' } }, '', '', '',
               { content: pdfR(totalFreeLoss), styles: { fontStyle: 'bold' } }]
            ],
            styles: { fontSize: 8 },
            headStyles: { fillColor: [200, 100, 0] },
            margin: { left: 14, right: 14 },
          })
          y = doc.lastAutoTable.finalY + 10
        }

        // ── SECTION 3: SUMMARY ──
        if (y > ph - 50) { doc.addPage(); y = 20 }
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(11)
        doc.text('SUMMARY', 14, y)
        y += 7
        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        if (totalShortfall > 0) {
          doc.text(`Total Stock Shortfall: ${pdfR(totalShortfall)}`, 14, y); y += 6
        }
        if (totalDiscountLoss > 0) {
          doc.text(`Total Discount Loss: ${pdfR(totalDiscountLoss)}`, 14, y); y += 6
        }
        if (totalFreeLoss > 0) {
          doc.text(`Total Free-Item Loss: ${pdfR(totalFreeLoss)}`, 14, y); y += 6
        }
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(12)
        doc.setTextColor(180, 50, 40)
        doc.text(`TOTAL MONEY UNACCOUNTED FOR: ${pdfR(totalUnaccounted)}`, 14, y)
        doc.setTextColor(0)
        y += 15

        // ── Per-category detail for stock shortfalls ──
        for (const c of shortCats) {
          if (y > ph - 60) { doc.addPage(); y = 20 }

          doc.setFontSize(10)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(180, 50, 40)
          doc.text(`${pdfSafe(c.label)} - Stock Shortfall`, 14, y)
          doc.setTextColor(0)
          y += 6

          doc.setFontSize(9)
          doc.setFont('helvetica', 'normal')
          doc.text(`Projected (${pdfR(c.projectedSales)}) - Consumed (${pdfR(c.consumed)}) - Surplus (${pdfR(c.surplusValue)}) = ${pdfR(c.variance)} SHORT`, 14, y)
          y += 6
          doc.text(`Opening: ${c.openingValue} ${c.openingUnit || ''} -> Projected: ${pdfR(c.projectedSales)}`, 14, y)
          y += 7

          if (c.salesDetail && c.salesDetail.length > 0) {
            autoTable(doc, {
              startY: y,
              head: [['Code', 'Product', 'Qty', 'Formula', 'Consumed']],
              body: c.salesDetail.map(d => [pdfSafe(d.code), pdfSafe(d.name), d.qty, pdfSafe(d.formula), pdfR(d.meatAmount)]),
              styles: { fontSize: 7 },
              headStyles: { fillColor: [60, 60, 60] },
              margin: { left: 14, right: 14 },
            })
            y = doc.lastAutoTable.finalY + 5
          }
          y += 5
        }
      }

      // Footer on each page
      const pageCount = doc.internal.getNumberOfPages()
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        doc.setFontSize(8)
        doc.setTextColor(150)
        doc.text(`Generated by Desres OS - ${new Date().toLocaleString()}`, pw / 2, ph - 10, { align: 'center' })
        if (pageCount > 1) {
          doc.text(`Page ${i} of ${pageCount}`, pw - 14, ph - 10, { align: 'right' })
        }
      }

      doc.save(`shortfall-report-${date}.pdf`)
      addToast('Shortfall report downloaded', 'success')
    } catch (err) {
      console.error('Shortfall PDF generation failed:', err)
      addToast('PDF export failed', 'error')
    }
  }

  const unresolvedHigh = data.flags.filter(f => f.severity === 'HIGH' && !f.resolved)
  const unresolvedMedium = data.flags.filter(f => f.severity === 'MEDIUM' && !f.resolved)
  const unresolvedLow = data.flags.filter(f => f.severity === 'LOW' && !f.resolved)

  return (
    <div className="space-y-6">
      {!dateProp && (
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Daily Operations</h1>
          <DateInput value={date} onChange={setDate} />
          {/* Steak service mode badge — kitchen only */}
          {!isBakery && (
            <span
              className="text-xs text-gray-500 ml-1"
              title={`Steak service mode for ${date}`}
            >
              {STEAK_MODE_HEADER_LABEL[getVorsWithSteak(currentDepartmentId || 'kitchen', date) ?? 'vors']}
            </span>
          )}
        </div>
      )}
      {dateProp && (
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold">Daily Sales &amp; Analysis</h2>
          {/* Steak service mode badge — kitchen only */}
          {!isBakery && (
            <span
              className="text-xs text-gray-500"
              title={`Steak service mode for ${date}`}
            >
              {STEAK_MODE_HEADER_LABEL[getVorsWithSteak(currentDepartmentId || 'kitchen', date) ?? 'vors']}
            </span>
          )}
          {syncing && (
            <span className="flex items-center gap-1.5 text-xs text-gray-500">
              <span className="animate-spin h-3 w-3 border border-gray-500 border-t-transparent rounded-full" />
              Syncing...
            </span>
          )}
        </div>
      )}

      {/* Historical date banner */}
      {flagsSuppressed && (
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 flex items-center gap-3">
          <span className="text-gray-500 shrink-0">🕐</span>
          <div>
            <p className="text-sm text-gray-400">Data before {dataStartDate} may be incomplete. Flags are hidden for this date.</p>
            <p className="text-xs text-gray-500">Re-upload the original POS PDF to restore accurate data for historical dates.</p>
          </div>
        </div>
      )}

      {/* Reconciliation Banner — only for dates after dataStartDate */}
      {!flagsSuppressed && (() => {
        const totProj = data.catSummaries.reduce((s, c) => s + (c.projectedSales || 0), 0)
        const totSurplus = data.catSummaries.reduce((s, c) => s + (c.surplusValue || 0), 0)
        const totConsumed = data.catSummaries.reduce((s, c) => s + (c.consumed || 0), 0)
        const diff = totProj - totConsumed - totSurplus
        if (totProj > 0 && diff > 2) {
          return (
            <div className="bg-red-900/30 border border-red-800 rounded-lg p-3 flex items-center gap-3">
              <AlertTriangle size={18} className="text-red-400 shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-300">{formatR(diff)} unaccounted for today</p>
                <p className="text-xs text-red-400/70">Projected {formatR(totProj)} − Consumed {formatR(totConsumed)} − Surplus {formatR(totSurplus)}</p>
              </div>
            </div>
          )
        }
        return null
      })()}

      {/* Day Score */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <p className="text-xs text-gray-400">Total Sales</p>
          <p className="text-xl font-bold text-green-400">{formatR(data.totalSales)}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <p className="text-xs text-gray-400">vs 30-Day Avg</p>
          <div className="flex items-center gap-2">
            <p className={`text-xl font-bold ${data.totalSales >= data.avg30 ? 'text-green-400' : 'text-red-400'}`}>
              {data.avg30 > 0 ? `${((data.totalSales / data.avg30 - 1) * 100).toFixed(0)}%` : '—'}
            </p>
            {data.totalSales >= data.avg30 ? <TrendingUp size={16} className="text-green-400" /> : <TrendingDown size={16} className="text-red-400" />}
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <p className="text-xs text-gray-400">Transactions</p>
          <p className="text-xl font-bold">{data.sales.length}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <p className="text-xs text-gray-400">Flags</p>
          <p className="text-xl font-bold text-amber-400">{data.flags.filter(f => !f.resolved).length}</p>
        </div>
      </div>

      {/* Morning Stock / Production */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <h3 className="text-sm font-medium mb-3">{isBakery ? 'Production' : 'Morning Stock'}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {data.morning.map(m => (
            <div key={m.category} className="bg-gray-800/50 rounded px-3 py-2">
              <span className="text-xs text-gray-400 capitalize">{m.category}</span>
              <p className="text-sm font-medium">{m.value} {UNIT_LABELS[m.unit]}</p>
            </div>
          ))}
          {data.morning.length === 0 && <p className="text-gray-600 text-sm col-span-4">{isBakery ? 'No production log for this date' : 'No morning log for this date'}</p>}
        </div>
      </div>

      {/* Sales Log */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <h3 className="text-sm font-medium mb-3">Sales Log ({data.sales.length} items)</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 text-xs border-b border-gray-800">
                <th className="text-left py-2 px-2">Code</th>
                <th className="text-left py-2 px-2">Product</th>
                <th className="text-left py-2 px-2">Category</th>
                <th className="text-right py-2 px-2">Qty</th>
                <th className="text-right py-2 px-2">Sales (R)</th>
                <th className="text-right py-2 px-2">Expected</th>
                <th className="text-right py-2 px-2">Diff</th>
              </tr>
            </thead>
            <tbody>
              {data.sales.map(s => {
                const expected = s.unitPrice && s.qtySold ? s.unitPrice * s.qtySold : null
                const diff = expected ? s.salesRands - expected : null
                return (
                  <tr key={s.id} className={`border-b border-gray-800/50 ${diff && diff !== 0 ? 'bg-amber-950/10' : ''}`}>
                    <td className="py-2 px-2 font-mono text-xs">{s.productCode}</td>
                    <td className="py-2 px-2">{s.productName}</td>
                    <td className="py-2 px-2 capitalize text-gray-400">{s.category}</td>
                    <td className="py-2 px-2 text-right">{s.qtySold}</td>
                    <td className="py-2 px-2 text-right">{formatR(s.salesRands)}</td>
                    <td className="py-2 px-2 text-right text-gray-400">{expected ? formatR(expected) : '—'}</td>
                    <td className={`py-2 px-2 text-right text-xs ${diff > 0 ? 'text-green-400' : diff < 0 ? 'text-red-400' : 'text-gray-500'}`}>
                      {diff !== null ? `${diff >= 0 ? '+' : ''}R${diff}` : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {data.sales.length === 0 && <p className="text-gray-600 text-sm text-center py-4">No sales for this date</p>}
        </div>
      </div>

      {/* Reconciliation */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <h3 className="text-sm font-medium mb-3">Category Reconciliation</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 text-xs border-b border-gray-800">
                <th className="text-left py-2 px-2">Category</th>
                <th className="text-right py-2 px-2">Projected</th>
                <th className="text-right py-2 px-2">Surplus</th>
                {isBakery && <th className="text-right py-2 px-2">Damages</th>}
                <th className="text-right py-2 px-2">Consumed</th>
                {!isBakery && <th className="text-right py-2 px-2" title="Expected sales minus actual sales collected">Sales Var.</th>}
                <th className="text-right py-2 px-2">Difference</th>
                <th className="text-center py-2 px-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.catSummaries.map(c => {
                const diff = c.variance
                const st = c.reconStatus
                const isExpanded = expandedCat === c.category
                const hasDetail = diff !== null && diff !== 0
                const colCount = isBakery ? 7 : 7
                return (
                <React.Fragment key={c.category}>
                <tr
                  className={`border-b border-gray-800/50 ${hasDetail ? 'cursor-pointer hover:bg-gray-800/30' : ''}`}
                  onClick={() => hasDetail && setExpandedCat(isExpanded ? null : c.category)}
                >
                  <td className="py-2 px-2 capitalize">
                    <span className="flex items-center gap-1">
                      {hasDetail && (isExpanded ? <ChevronDown size={14} className="text-gray-500" /> : <ChevronRight size={14} className="text-gray-500" />)}
                      {c.label}
                    </span>
                    {/* Mode-driven note for vors on no_vors / extra_steak days */}
                    {c.displayNote && (
                      <span className="text-[10px] text-gray-400 italic block ml-5 mt-0.5">
                        {c.displayNote}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-right">{c.projectedSales !== null ? formatR(c.projectedSales) : 'N/A'}</td>
                  <td className="py-2 px-2 text-right">{c.surplusValue ? formatR(c.surplusValue) : '—'}</td>
                  {isBakery && <td className="py-2 px-2 text-right text-red-400/70">{c.damagesValue || '—'}</td>}
                  <td className="py-2 px-2 text-right">{formatR(c.consumed)}</td>
                  {!isBakery && (
                    <td className={`py-2 px-2 text-right text-xs ${c.salesVariance > 0 ? 'text-amber-400' : c.salesVariance < 0 ? 'text-green-400' : 'text-gray-500'}`}>
                      {c.salesVariance != null && c.salesVariance !== 0 ? (
                        <span>{c.salesVariance > 0 ? '−' : '+'}{formatR(Math.abs(c.salesVariance))}</span>
                      ) : '—'}
                    </td>
                  )}
                  <td className={`py-2 px-2 text-right text-xs font-medium ${flagsSuppressed ? 'text-gray-500' : st === 'short' ? 'text-red-400' : st === 'reconciled' ? 'text-green-400' : 'text-gray-500'}`}>
                    {diff === null ? '—'
                    : flagsSuppressed ? (
                      formatR(diff)
                    ) : st === 'reconciled' ? (
                      <span className="flex items-center justify-end gap-1"><CheckCircle size={12} className="text-green-400" /> Reconciled</span>
                    ) : st === 'short' ? (
                      <span className="flex items-center justify-end gap-1"><span className="text-red-400">✗</span> SHORT {formatR(diff)}</span>
                    ) : '—'}
                  </td>
                  <td className="py-2 px-2 text-center">
                    {flagsSuppressed ? null
                    : c.restockImplied ? (
                      <span className="text-xs bg-blue-900 text-blue-300 px-1.5 py-0.5 rounded">Restock</span>
                    ) : st === 'reconciled' ? (
                      <CheckCircle size={14} className="text-green-400 inline" />
                    ) : st === 'short' ? (
                      <AlertTriangle size={14} className="text-red-400 inline" />
                    ) : null}
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={colCount} className="p-0">
                      <div className="bg-gray-800/40 border-y border-gray-700 px-4 py-4 space-y-4 text-sm">
                        {/* 1. Short equation */}
                        <div className={`px-3 py-2 rounded text-sm font-medium ${st === 'short' ? 'bg-red-900/20 border border-red-800/50 text-red-300' : 'bg-gray-700/30 border border-gray-700 text-gray-300'}`}>
                          Projected ({formatR(c.projectedSales)}) − Consumed ({formatR(c.consumed)}) − Surplus ({formatR(c.surplusValue)}) = {formatR(diff)} {st === 'short' ? 'SHORT' : 'over'}
                        </div>

                        {/* 2. Morning Log detail */}
                        <div>
                          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Morning Log</h4>
                          <p className="text-gray-300">
                            Opening: {c.openingValue} {c.openingUnit || ''} → Projected: {formatR(c.projectedSales)}
                          </p>
                        </div>

                        {/* 3. Sales breakdown */}
                        <div>
                          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Sales Breakdown ({(c.salesDetail || []).length} transactions)</h4>
                          {c.salesDetail && c.salesDetail.length > 0 ? (
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-gray-500 border-b border-gray-700">
                                  <th className="text-left py-1 px-2">Code</th>
                                  <th className="text-left py-1 px-2">Product</th>
                                  <th className="text-right py-1 px-2">Qty</th>
                                  <th className="text-right py-1 px-2">Expected</th>
                                  <th className="text-right py-1 px-2">Actual (POS)</th>
                                  <th className="text-right py-1 px-2">Diff</th>
                                  <th className="text-left py-1 px-2">Formula</th>
                                </tr>
                              </thead>
                              <tbody>
                                {c.salesDetail.map((d, i) => {
                                  // "Actual (POS)" = what the customer paid (sale.salesRands).
                                  // d.meatAmount is the pool-consumed amount (cross-pool alloc
                                  // for steak/vors) — that belongs in reconciliation math, not
                                  // in this column.
                                  const actualPos = d.posAmount ?? 0
                                  const saleDiff = (d.expectedAmount || 0) - actualPos
                                  return (
                                  <tr key={i} className="border-b border-gray-800/30">
                                    <td className="py-1 px-2 font-mono">{d.code}</td>
                                    <td className="py-1 px-2">{d.name}</td>
                                    <td className="py-1 px-2 text-right">{d.qty}</td>
                                    <td className="py-1 px-2 text-right text-gray-400">{formatR(d.expectedAmount || 0)}</td>
                                    <td className="py-1 px-2 text-right font-medium">{formatR(actualPos)}</td>
                                    <td className={`py-1 px-2 text-right ${saleDiff > 0 ? 'text-amber-400' : saleDiff < 0 ? 'text-green-400' : 'text-gray-500'}`}>
                                      {saleDiff !== 0 ? `${saleDiff > 0 ? '−' : '+'}${formatR(Math.abs(saleDiff))}` : '—'}
                                    </td>
                                    <td className="py-1 px-2 text-gray-400">{d.formula}</td>
                                  </tr>
                                  )
                                })}
                                <tr className="border-t border-gray-700 font-semibold">
                                  <td colSpan={3} className="py-1 px-2 text-right">Totals:</td>
                                  <td className="py-1 px-2 text-right text-gray-400">{formatR(c.totalPosExpected || 0)}</td>
                                  <td className="py-1 px-2 text-right">{formatR(c.totalPosActual || 0)}</td>
                                  <td className={`py-1 px-2 text-right ${(c.salesVariance || 0) > 0 ? 'text-amber-400' : (c.salesVariance || 0) < 0 ? 'text-green-400' : ''}`}>
                                    {(c.salesVariance || 0) !== 0 ? `${c.salesVariance > 0 ? '−' : '+'}${formatR(Math.abs(c.salesVariance))}` : '—'}
                                  </td>
                                  <td />
                                </tr>
                              </tbody>
                            </table>
                          ) : (
                            <p className="text-gray-500">No sales transactions for this category.</p>
                          )}
                        </div>

                        {/* 4. Surplus detail */}
                        <div>
                          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Surplus</h4>
                          <p className="text-gray-300">{c.surplusValue > 0 ? `Surplus: ${formatR(c.surplusValue)}` : 'No surplus recorded.'}</p>
                        </div>

                        {/* 5. Net summary */}
                        <div className={`px-3 py-2 rounded text-sm ${st === 'short' ? 'bg-red-900/20 border border-red-800/50 text-red-300' : 'bg-gray-700/30 border border-gray-700 text-gray-300'}`}>
                          <p>Projected {formatR(c.projectedSales)} − Consumed {formatR(c.consumed)} − Surplus {formatR(c.surplusValue)} = {formatR(diff)}.{' '}
                          {st === 'short' ? 'This amount is unaccounted for.' : 'Category is reconciled.'}</p>
                          {(c.salesVariance || 0) > 0 && (
                            <p className="mt-1 text-amber-400 text-xs">Sales variance: {formatR(c.salesVariance)} under-collected vs expected prices.</p>
                          )}
                        </div>

                        {/* 6. PDF download */}
                        <button
                          onClick={(e) => { e.stopPropagation(); downloadBreakdownPdf(c) }}
                          className="flex items-center gap-2 text-xs text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 px-3 py-1.5 rounded transition-colors"
                        >
                          <Download size={14} /> Download PDF
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
                )
              })}
              {/* Totals Row */}
              {(() => {
                const totProj = data.catSummaries.reduce((s, c) => s + (c.projectedSales || 0), 0)
                const totSurplus = data.catSummaries.reduce((s, c) => s + (c.surplusValue || 0), 0)
                const totConsumed = data.catSummaries.reduce((s, c) => s + (c.consumed || 0), 0)
                const totSalesVar = data.catSummaries.reduce((s, c) => s + (c.salesVariance || 0), 0)
                const totDiff = totProj > 0 ? totProj - totConsumed - totSurplus : null
                const totSt = totDiff !== null ? (totDiff > 2 ? 'short' : 'reconciled') : null
                return (
                  <tr className="border-t-2 border-gray-700 bg-gray-800/50 font-semibold">
                    <td className="py-2 px-2">Total</td>
                    <td className="py-2 px-2 text-right text-gray-400">{totProj > 0 ? formatR(totProj) : '—'}</td>
                    <td className="py-2 px-2 text-right">{totSurplus > 0 ? formatR(totSurplus) : '—'}</td>
                    {isBakery && <td className="py-2 px-2" />}
                    <td className="py-2 px-2 text-right">{formatR(totConsumed)}</td>
                    {!isBakery && (
                      <td className={`py-2 px-2 text-right text-xs ${totSalesVar > 0 ? 'text-amber-400' : totSalesVar < 0 ? 'text-green-400' : 'text-gray-500'}`}>
                        {totSalesVar !== 0 ? (
                          <span>{totSalesVar > 0 ? '−' : '+'}{formatR(Math.abs(totSalesVar))}</span>
                        ) : '—'}
                      </td>
                    )}
                    <td className={`py-2 px-2 text-right text-xs ${flagsSuppressed ? 'text-gray-500' : totSt === 'short' ? 'text-red-400' : totSt === 'reconciled' ? 'text-green-400' : ''}`}>
                      {totDiff === null ? '—'
                      : flagsSuppressed ? formatR(totDiff)
                      : totSt === 'reconciled' ? (
                        <span className="flex items-center justify-end gap-1"><CheckCircle size={12} className="text-green-400" /> Reconciled</span>
                      ) : totSt === 'short' ? (
                        <span className="flex items-center justify-end gap-1"><span className="text-red-400">✗</span> SHORT {formatR(totDiff)}</span>
                      ) : '—'}
                    </td>
                    <td className="py-2 px-2" />
                  </tr>
                )
              })()}
            </tbody>
          </table>
        </div>

      </div>

      {/* PAP Breakdown */}
      {!isBakery && (data.papFromMeals > 0 || data.papStandalone > 0) && (() => {
        // Pap warmers from the meta doc cache (sync read). null when staff
        // haven't entered it yet for this date — both new columns show '—'
        // in that state. Computed inside the IIFE so it re-reads on every
        // render after backgroundSyncForDate bumps syncKey.
        const papWarmers = getPapWarmers(currentDepartmentId, date)
        const hasWarmers = papWarmers != null && papWarmers > 0
        const revPerWarmer = hasWarmers
          ? Math.round((data.papTotal / papWarmers) * 100) / 100
          : null
        return (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-medium mb-3">PAP Contribution</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-3">
            <div className="bg-gray-800/50 rounded px-3 py-2">
              <span className="text-xs text-gray-400">From Meals</span>
              <p className="text-sm font-medium text-amber-400">{formatR(data.papFromMeals)}</p>
            </div>
            <div className="bg-gray-800/50 rounded px-3 py-2">
              <span className="text-xs text-gray-400">Standalone (PAP only)</span>
              <p className="text-sm font-medium">{formatR(data.papStandalone)}</p>
            </div>
            <div className="bg-gray-800/50 rounded px-3 py-2">
              <span className="text-xs text-gray-400">Total PAP</span>
              <p className="text-sm font-bold text-white">{formatR(data.papTotal)}</p>
            </div>
            <div className="bg-gray-800/50 rounded px-3 py-2">
              <span className="text-xs text-gray-400">Warmers Prepared</span>
              <p className="text-sm font-medium text-white">
                {papWarmers != null
                  ? (Number.isInteger(papWarmers) ? String(papWarmers) : papWarmers.toFixed(1))
                  : '—'}
              </p>
            </div>
            <div className="bg-gray-800/50 rounded px-3 py-2">
              <span className="text-xs text-gray-400">R / Warmer</span>
              <p className="text-sm font-medium text-white">
                {revPerWarmer != null ? `R${revPerWarmer}/warmer` : '—'}
              </p>
            </div>
          </div>
          {data.papMealDetails.length > 0 && (
            <details className="text-xs">
              <summary className="text-gray-400 cursor-pointer hover:text-white">Meal breakdown ({data.papMealDetails.length} items)</summary>
              <table className="w-full mt-2 text-xs">
                <thead>
                  <tr className="text-gray-500 border-b border-gray-800">
                    <th className="text-left py-1 px-2">Product</th>
                    <th className="text-right py-1 px-2">Qty</th>
                    <th className="text-right py-1 px-2">Size</th>
                    <th className="text-right py-1 px-2">PAP Cost</th>
                    <th className="text-right py-1 px-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.papMealDetails.map((d, i) => (
                    <tr key={i} className="border-b border-gray-800/30">
                      <td className="py-1 px-2">{d.name}</td>
                      <td className="py-1 px-2 text-right">{d.qty}</td>
                      <td className="py-1 px-2 text-right capitalize">{d.size}</td>
                      <td className="py-1 px-2 text-right text-gray-400">{formatR(d.cost)}</td>
                      <td className="py-1 px-2 text-right">{formatR(d.contribution)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
        )
      })()}

      {/* Flags — hidden for historical dates */}
      {!flagsSuppressed && data.flags.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-medium mb-3">Flags ({data.flags.filter(f => !f.resolved).length} unresolved)</h3>
          <div className="space-y-2">
            {[...unresolvedHigh, ...unresolvedMedium, ...unresolvedLow].map(f => (
              <div key={f.id} className={`flex items-center gap-3 px-3 py-2 rounded border ${getSeverityColor(f.severity)}`}>
                <span className={`text-xs px-1.5 py-0.5 rounded ${getSeverityBadge(f.severity)}`}>{f.severity}</span>
                <span className="text-xs font-mono">{f.flagCode}</span>
                <span className="text-xs flex-1">{f.description}</span>
                <button
                  onClick={() => handleResolve(f.id)}
                  className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded bg-gray-800"
                >
                  Resolve
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Surplus */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <h3 className="text-sm font-medium mb-3">End-of-Day Surplus</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {data.surplus.map(s => (
            <div key={s.category} className="bg-gray-800/50 rounded px-3 py-2">
              <span className="text-xs text-gray-400 capitalize">{s.category}</span>
              <p className="text-sm font-medium">{s.value} {UNIT_LABELS[s.unit]}</p>
            </div>
          ))}
          {data.surplus.length === 0 && <p className="text-gray-600 text-sm col-span-4">No surplus log for this date</p>}
        </div>
      </div>

      {/* Download Bar — always visible at bottom */}
      {(() => {
        const shortCats = data.catSummaries.filter(c => c.reconStatus === 'short')
        const hasShortfall = shortCats.length > 0
        const totalShort = shortCats.reduce((s, c) => s + (c.variance || 0), 0)

        // Discount losses: categories where salesVariance < -2 (sold below expected price)
        // Only includes categories with products that have fixed prices (not continuous_mirror)
        const discountCats = data.catSummaries.filter(c =>
          (c.salesVariance || 0) < -2 && (c.totalPosExpected || 0) > 0
        )

        // Per-product discount breakdown — single source of truth used by
        // both this screen and the PDF export. Uses the canonical helper
        // so the on-screen totals match what the PDF shows.
        const screenDiscountResult = computeDiscountShortages(data.sales || [], liveProducts || [], currentDepartmentId)
        const screenDiscountItems = screenDiscountResult.items
        const screenFreeItems = screenDiscountResult.freeItems
        const screenTotalDiscount = screenDiscountResult.totalDiscount
        const screenTotalFreeLoss = screenDiscountResult.totalFreeLoss

        const hasDiscountLoss = screenDiscountItems.length > 0 || screenFreeItems.length > 0
        const totalDiscountLoss = screenTotalDiscount
        const totalUnaccounted = totalShort + totalDiscountLoss + screenTotalFreeLoss
        const hasAnyLoss = hasShortfall || hasDiscountLoss

        const handleExcelExport = async () => {
          try {
            const XLSX = (await import('xlsx')).default || await import('xlsx')
            const wb = XLSX.utils.book_new()
            // Reconciliation summary sheet
            const reconRows = data.catSummaries.map(c => ({
              Category: c.label,
              Projected: c.projectedSales,
              Consumed: c.consumed,
              Surplus: c.surplusValue,
              ...(isBakery ? {} : { 'Expected Sales': c.expectedSales || 0, 'Sales Variance': c.salesVariance || 0 }),
              Difference: c.variance,
              Status: c.reconStatus || '—',
            }))
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(reconRows), 'Reconciliation')
            // Sales detail sheet
            const salesRows = data.sales.map(s => ({
              Code: s.productCode,
              Product: s.productName,
              Category: s.category,
              Qty: s.qtySold,
              'Sales (R)': s.salesRands,
              'Unit Price': s.unitPrice || '',
            }))
            if (salesRows.length > 0) {
              XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(salesRows), 'Sales')
            }
            XLSX.writeFile(wb, `daily-report-${currentDepartmentId}-${date}.xlsx`)
            addToast('Excel report downloaded', 'success')
          } catch (err) {
            console.error('Excel export failed:', err)
            addToast('Excel export failed', 'error')
          }
        }

        return (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3">
            {/* Loss summary */}
            <div className="space-y-1">
              {hasShortfall && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-red-400">Stock Shortfalls — {shortCats.length} categor{shortCats.length === 1 ? 'y' : 'ies'}</span>
                  <span className="text-red-400 font-medium">{formatR(totalShort)}</span>
                </div>
              )}
              {hasDiscountLoss && (
                <>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-amber-400">
                      Discount Losses — {screenDiscountItems.length} product{screenDiscountItems.length !== 1 ? 's' : ''} sold below price
                    </span>
                    <span className="text-amber-400 font-medium">{formatR(totalDiscountLoss)}</span>
                  </div>
                  {screenDiscountItems.length > 0 && (
                    <div className="mt-1 ml-2 border-l-2 border-amber-700/40 pl-3 space-y-0.5">
                      {screenDiscountItems.map(item => (
                        <div key={item.productCode} className="flex items-center justify-between text-[11px] text-amber-200/80">
                          <span>
                            <span className="text-gray-300">{item.productName}</span>
                            <span className="text-gray-600 ml-1.5 font-mono text-[10px]">{item.productCode}</span>
                            <span className="text-gray-500 ml-2">{item.qty}× @ {formatR(item.actualPrice)} (expected {formatR(item.expectedPrice)})</span>
                          </span>
                          <span className="text-red-400 font-medium">{formatR(item.totalDiscount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {screenFreeItems.length > 0 && (
                    <>
                      <div className="flex items-center justify-between text-xs mt-1">
                        <span className="text-amber-400">
                          Items given away free — {screenFreeItems.length} product{screenFreeItems.length !== 1 ? 's' : ''}
                        </span>
                        <span className="text-amber-400 font-medium">{formatR(screenTotalFreeLoss)}</span>
                      </div>
                      <div className="mt-1 ml-2 border-l-2 border-amber-700/40 pl-3 space-y-0.5">
                        {screenFreeItems.map(item => (
                          <div key={item.productCode} className="flex items-center justify-between text-[11px] text-amber-200/80">
                            <span>
                              <span className="text-gray-300">{item.productName}</span>
                              <span className="text-gray-600 ml-1.5 font-mono text-[10px]">{item.productCode}</span>
                              <span className="text-gray-500 ml-2">{item.qty}× free</span>
                            </span>
                            <span className="text-red-400 font-medium">{formatR(item.totalLoss)}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
              {hasAnyLoss ? (
                <div className="flex items-center justify-between text-xs font-bold border-t border-gray-700 pt-1">
                  <span className="text-red-300">TOTAL MONEY UNACCOUNTED FOR</span>
                  <span className="text-red-300">{formatR(totalUnaccounted)}</span>
                </div>
              ) : (
                <div className="text-xs text-green-400">All categories reconciled — no shortfalls or discount losses</div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => downloadShortfallReportPdf(data.catSummaries, shortCats, discountCats, data.sales, liveProducts)}
                className={`flex items-center gap-2 text-xs px-4 py-2 rounded transition-colors border ${
                  hasAnyLoss
                    ? 'text-red-400 hover:text-white bg-red-900/20 hover:bg-red-900/40 border-red-800/50'
                    : 'text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 border-gray-700'
                }`}
              >
                <Download size={14} />
                Download Shortfall PDF
              </button>
                <button
                  onClick={handleExcelExport}
                  className="flex items-center gap-2 text-xs text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 border border-gray-700 px-4 py-2 rounded transition-colors"
                >
                  <FileSpreadsheet size={14} />
                  Download Excel
                </button>
              </div>
            </div>
        )
      })()}
    </div>
  )
}

